'use client'

import { createContext, useContext, useEffect, useMemo, useState } from 'react'
import type { StreamNews } from '@/lib/stock/streamNews'
import { newsImageSrc } from '@/lib/news/newsImage'
import { Newspaper, Zap, MessageCircle, TrendingUp, TrendingDown, Pause, Play, LineChart, Bot, Activity, Filter } from 'lucide-react'
import { cleanAlertMessage } from '@/lib/alerts/thothMessage'
import { activityTimeMs, mergeActivity, OVERVIEW_LIMIT } from '@/lib/stock/activitySelection'

/**
 * StockActivityStream — the signature per-stock feature: news + unusual-volume alerts (RVOL, movers,
 * EMA-trend, halts, AI-trader trades) + community posts merged into ONE "what's happening with $SYMBOL"
 * timeline, with one-click CATEGORY FILTERS. Client component so filtering is instant, but its initial
 * render is SSR'd from props the ticker page already fetches (crawlable, no extra queries). Alert colors
 * line up with the live-alerts theme (amber); movers show their PRICE MOVE %, RVOL rows show RVOL×.
 */

type Alert = {
  message: string | null; triggeredBy: string | null; alertType: string | null
  currentPrice: number | string | null; rvol: number | string | null; createdAt: string | null
  percentChange?: number | string | null; ahPercentChange?: number | string | null
  delta?: number | string | null; emaPercentDiff?: number | string | null; alertLevel?: number | null
}
type Post = {
  id: string; body: string; userName: string | null; source: string | null; createdAt: string | null
  userImage: string | null; donorTier: string | null; level: number | null
}
type Item =
  | { kind: 'news'; t: number; a: StreamNews }
  | { kind: 'alert'; t: number; a: Alert }
  | { kind: 'post'; t: number; a: Post }

type Cat = 'news' | 'chat' | 'rvol' | 'mover' | 'ema' | 'halt' | 'ai'

// The SAME timestamp rule and order the server uses to pick what the hidden hub tabs still carry.
// See src/lib/stock/activitySelection.ts.
const toMs = activityTimeMs
function timeAgo(ms: number, now: number): string {
  if (!ms) return ''
  const m = Math.floor((now - ms) / 60000)
  if (m < 1) return 'just now'
  if (m < 60) return `${m}m ago`
  const h = Math.floor(m / 60)
  if (h < 24) return `${h}h ago`
  const d = Math.floor(h / 24)
  return `${d}d ago`
}
// Exact ET stamp — shown alongside the relative time so alerts are unambiguous.
// The formatter is built ONCE: per call it was 217ms of phone CPU on a stock page (twice per row),
// register intl-formatter-per-call-client-1031.
const EXACT_ET_FMT = new Intl.DateTimeFormat('en-US', { timeZone: 'America/New_York', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true })
function fmtExact(ms: number): string {
  if (!ms) return ''
  return EXACT_ET_FMT.format(new Date(ms)) + ' ET'
}
const num = (v: number | string | null | undefined) => { const n = Number(v); return Number.isFinite(n) ? n : null }
// 'rider' is the Watcher's LIVE stored alert_type (pre-2026-07-25 persona, written ~3,600/wk by
// topPlayAlert.ts, deliberately never renamed - see seo/alertLabel.ts). Without it the AI's own
// paper trades fall through to the RVOL tab/category instead of landing under "AI Traders".
const AI_SOURCES = ['watcher', 'rider', 'thoth', 'ra', 'osiris', 'solomon']

function alertCat(a: Alert): Cat {
  const key = (a.alertType || a.triggeredBy || '').toLowerCase().trim()
  if (AI_SOURCES.includes(key)) return 'ai'
  if (key.includes('halt') || key.includes('resume')) return 'halt'
  if (key.includes('ema')) return 'ema'
  if (key.includes('mover') || key.includes('momentum') || key.includes('price')) return 'mover'
  return 'rvol'
}
function catOf(i: Item): Cat {
  return i.kind === 'news' ? 'news' : i.kind === 'post' ? 'chat' : alertCat(i.a)
}

// Icon + accent per alert category. Alerts share the amber "live-alert" theme (movers no longer blue);
// EMA stays violet (matches the EMA panel), halts red / resumes green, AI-trader trades amber-bot.
function alertStyle(a: Alert) {
  const cat = alertCat(a)
  if (cat === 'ai') return { Icon: Bot, ring: 'bg-amber-500/15 text-amber-700 dark:text-amber-300 ring-amber-500/30' }
  if (cat === 'halt') {
    const isResume = (a.alertType || a.triggeredBy || '').toLowerCase().includes('resume')
    return isResume
      ? { Icon: Play, ring: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 ring-emerald-500/30' }
      : { Icon: Pause, ring: 'bg-rose-500/15 text-rose-600 dark:text-rose-300 ring-rose-500/30' }
  }
  if (cat === 'ema') return { Icon: LineChart, ring: 'bg-violet-500/15 text-violet-700 dark:text-violet-300 ring-violet-500/30' }
  if (cat === 'mover') {
    const mv = num(a.percentChange) ?? num(a.ahPercentChange) ?? num(a.delta) ?? 0
    return { Icon: mv >= 0 ? TrendingUp : TrendingDown, ring: 'bg-amber-500/15 text-amber-700 dark:text-amber-300 ring-amber-500/30' }
  }
  return { Icon: Zap, ring: 'bg-amber-500/15 text-amber-700 dark:text-amber-300 ring-amber-500/30' } // RVOL
}

// The far-right metric: movers show move %, RVOL shows RVOL×, EMA shows % vs EMA.
function alertMetric(a: Alert): { text: string; cls: string } | null {
  const cat = alertCat(a)
  if (cat === 'mover') {
    const mv = num(a.percentChange) ?? num(a.ahPercentChange) ?? num(a.delta)
    if (mv == null) return null
    return { text: `${mv >= 0 ? '+' : ''}${mv.toFixed(2)}%`, cls: mv >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400' }
  }
  if (cat === 'ema') {
    const e = num(a.emaPercentDiff)
    if (e == null) return null
    return { text: `${e >= 0 ? '+' : ''}${e.toFixed(1)}% vs EMA`, cls: 'text-violet-700 dark:text-violet-300' }
  }
  if (cat === 'rvol') {
    const r = num(a.rvol)
    if (r == null || r <= 0) return null
    return { text: `${r.toFixed(1)}× RVOL`, cls: 'text-amber-700 dark:text-amber-300' }
  }
  return null
}

function Rail({ children, className }: { children: React.ReactNode; className: string }) {
  return <div className={`relative flex h-8 w-8 shrink-0 items-center justify-center rounded-full ring-1 ${className}`}>{children}</div>
}
// 🚨 The clock rows age against. NEVER read Date.now() during render: the server and the browser read it at
// different moments, so "41m ago" could hydrate as "42m ago", React threw #425 and re-rendered this whole
// Suspense boundary on the client (every load for a visitor whose clock is a minute off). The first render uses
// the server's clock; the live one takes over after mount. Register stock-activity-clock-hydration-1103.
const NowCtx = createContext(0)
function Stamp({ ms }: { ms: number }) {
  const now = useContext(NowCtx)
  return <span title={fmtExact(ms)}>· {timeAgo(ms, now)} <span className="hidden text-muted-foreground sm:inline">· {fmtExact(ms)}</span></span>
}

function NewsRow({ a, symbol }: { a: StreamNews; symbol: string }) {
  const chip = a.sentiment === 'positive' ? { l: 'Bullish', c: 'text-emerald-700 dark:text-emerald-300' } : a.sentiment === 'negative' ? { l: 'Bearish', c: 'text-rose-600 dark:text-rose-300' } : a.sentiment === 'neutral' ? { l: 'Neutral', c: 'text-muted-foreground' } : null
  const thumb = newsImageSrc(a.imageUrl, symbol) ?? `/api/logo?symbol=${encodeURIComponent(symbol)}`
  return (
    <a href={a.url} target="_blank" rel="noopener noreferrer" className="group flex gap-3 rounded-xl border border-border bg-card p-2.5 transition-colors hover:border-muted-foreground/40 dark:hover:border-zinc-600 hover:bg-accent dark:hover:bg-zinc-800/50">
      <Rail className="bg-amber-500/15 text-amber-700 dark:text-amber-300 ring-amber-500/30"><Newspaper className="h-4 w-4" /></Rail>
      <div className="hidden h-14 w-20 shrink-0 overflow-hidden rounded-lg bg-muted sm:block">
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img src={thumb} alt="" className="h-full w-full object-cover" loading="lazy" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="mb-0.5 flex items-center gap-1.5 text-[11px] text-muted-foreground">
          <span className="font-semibold text-amber-700 dark:text-amber-300/80">News</span>
          <span>· {a.publisher.name}</span>
          <Stamp ms={toMs(a.publishedUtc)} />
          {chip && <span className={`ml-auto shrink-0 text-[10px] font-bold uppercase ${chip.c}`}>{chip.l}</span>}
        </div>
        <div className="line-clamp-2 text-sm font-bold leading-snug text-foreground group-hover:text-amber-600 dark:group-hover:text-amber-300">{a.title}</div>
      </div>
    </a>
  )
}

function AlertRow({ a }: { a: Alert }) {
  const { Icon, ring } = alertStyle(a)
  const px = num(a.currentPrice)
  const metric = alertMetric(a)
  return (
    <div className="flex gap-3 rounded-xl border border-border bg-card p-2.5">
      <Rail className={ring}><Icon className="h-4 w-4" /></Rail>
      <div className="min-w-0 flex-1">
        <div className="mb-0.5 flex items-center gap-1.5 text-[11px] text-muted-foreground">
          <span className="font-semibold text-amber-700 dark:text-amber-300/80">Alert</span>
          <Stamp ms={toMs(a.createdAt)} />
          {metric && <span className={`ml-auto shrink-0 font-semibold tabular-nums ${metric.cls}`}>{metric.text}</span>}
        </div>
        {/* ⚠ cleanAlertMessage, NOT a.message. Alert messages carry a __THOTH_JSON__ payload and
            this rendered it verbatim - measured on /stocks/HTZ 2026-08-23, 14 times on one page,
            exposing setup ids, share counts and "instance":"Rider", a persona retired 07-25. */}
        <div className="text-sm leading-snug text-foreground">{cleanAlertMessage(a.message) || 'Unusual activity'}</div>
        {px != null && px > 0 && <div className="mt-0.5 text-[11px] tabular-nums text-muted-foreground">at {px < 1 ? `$${px.toFixed(3)}` : `$${px.toFixed(2)}`}</div>}
      </div>
    </div>
  )
}

function PostRow({ a }: { a: Post }) {
  return (
    <div className="flex gap-3 rounded-xl border border-border bg-card p-2.5">
      <Rail className="bg-sky-500/15 text-sky-700 dark:text-sky-300 ring-sky-500/30">
        {a.userImage
          // eslint-disable-next-line @next/next/no-img-element
          ? <img src={a.userImage} alt="" className="h-8 w-8 rounded-full object-cover" loading="lazy" />
          : <MessageCircle className="h-4 w-4" />}
      </Rail>
      <div className="min-w-0 flex-1">
        <div className="mb-0.5 flex items-center gap-1.5 text-[11px] text-muted-foreground">
          <span className="font-semibold text-sky-700 dark:text-sky-300/90">{a.userName || 'Trader'}</span>
          <Stamp ms={toMs(a.createdAt)} />
        </div>
        <div className="whitespace-pre-wrap break-words text-sm leading-snug text-foreground">{a.body}</div>
      </div>
    </div>
  )
}

const CAT_LABELS: { id: Cat; label: string }[] = [
  { id: 'news', label: 'News' },
  { id: 'rvol', label: 'RVOL' },
  { id: 'mover', label: 'Movers' },
  { id: 'ema', label: 'EMA' },
  { id: 'halt', label: 'Halts' },
  { id: 'ai', label: 'AI Traders' },
  { id: 'chat', label: 'Chat' },
]

export default function StockActivityStream({
  symbol, news, alerts, posts, limit = OVERVIEW_LIMIT, bare = false, renderedAt,
}: {
  symbol: string; news: StreamNews[]; alerts: Alert[]; posts: Post[]; limit?: number
  /** Server clock at render (Date.now() in the page). Required so no caller can reintroduce a render-time clock read. */
  renderedAt: number
  /** Rows only, no heading or filter chips: used by the hidden hub tabs to carry the items Overview omits. */
  bare?: boolean
}) {
  const [filter, setFilter] = useState<Cat | 'all'>('all')
  const [now, setNow] = useState(renderedAt)
  useEffect(() => { setNow(Date.now()) }, [])

  const all = useMemo<Item[]>(() => mergeActivity(news, alerts, posts) as Item[], [news, alerts, posts])

  const counts = useMemo(() => {
    const c = {} as Record<Cat, number>
    for (const i of all) { const k = catOf(i); c[k] = (c[k] ?? 0) + 1 }
    return c
  }, [all])

  const shown = useMemo(
    () => (filter === 'all' ? all : all.filter((i) => catOf(i) === filter)).slice(0, limit),
    [all, filter, limit],
  )

  if (!all.length) return null
  const activeCats = CAT_LABELS.filter((c) => (counts[c.id] ?? 0) > 0)

  if (bare) {
    return (
      <NowCtx.Provider value={now}>
      <div className="space-y-2">
        {all.slice(0, limit).map((i) =>
          i.kind === 'news' ? <NewsRow key={`n-${i.a.id}`} a={i.a} symbol={symbol} />
          : i.kind === 'alert' ? <AlertRow key={`a-${i.t}-${i.a.message?.slice(0, 12)}`} a={i.a} />
          : <PostRow key={`p-${i.a.id}`} a={i.a} />,
        )}
      </div>
      </NowCtx.Provider>
    )
  }

  return (
    <NowCtx.Provider value={now}>
    <section>
      <h2 className="flex items-center gap-2 text-lg font-bold tracking-tight text-foreground">
        <span className="inline-block h-4 w-1 rounded-full bg-gradient-to-b from-amber-400 to-amber-600" />
        ${symbol} Activity
      </h2>
      <p className="mb-3 mt-0.5 text-[13px] text-muted-foreground">News, unusual-volume alerts, AI-trader trades and community chatter - the whole story, newest first.</p>

      {/* Category filters */}
      <div className="mb-3 flex flex-wrap items-center gap-1.5">
        <Filter className="h-3.5 w-3.5 text-muted-foreground" />
        <button type="button" onClick={() => setFilter('all')}
          className={`rounded-full border px-2.5 py-1 text-xs font-semibold transition-colors ${filter === 'all' ? 'border-amber-400 bg-amber-500/15 text-amber-700 dark:text-amber-300' : 'border-border bg-card text-muted-foreground hover:text-foreground'}`}>
          All <span className="text-muted-foreground">{all.length}</span>
        </button>
        {activeCats.map((c) => (
          <button key={c.id} type="button" onClick={() => setFilter(c.id)}
            className={`rounded-full border px-2.5 py-1 text-xs font-semibold transition-colors ${filter === c.id ? 'border-amber-400 bg-amber-500/15 text-amber-700 dark:text-amber-300' : 'border-border bg-card text-muted-foreground hover:text-foreground'}`}>
            {c.label} <span className="text-muted-foreground">{counts[c.id]}</span>
          </button>
        ))}
      </div>

      {shown.length === 0 ? (
        <div className="flex items-center justify-center gap-2 rounded-xl border border-border bg-card py-8 text-sm text-muted-foreground"><Activity className="h-4 w-4" /> Nothing in this category yet.</div>
      ) : (
        <div className="space-y-2">
          {shown.map((i) =>
            i.kind === 'news' ? <NewsRow key={`n-${i.a.id}`} a={i.a} symbol={symbol} />
            : i.kind === 'alert' ? <AlertRow key={`a-${i.t}-${i.a.message?.slice(0, 12)}`} a={i.a} />
            : <PostRow key={`p-${i.a.id}`} a={i.a} />,
          )}
        </div>
      )}
    </section>
    </NowCtx.Provider>
  )
}
